import {
  acceptedResponseSchema,
  currentUserSchema,
  deviceSessionListSchema,
  loginResponseSchema,
  mfaStatusSchema,
  recoveryCodesResponseSchema,
  revokedCountSchema,
  totpSetupResponseSchema,
  type CurrentUser,
  type EmailRequest,
  type LoginRequest,
  type LoginResponse,
  type MfaCodeRequest,
  type MfaLoginRequest,
  type PasswordChangeRequest,
  type PasswordResetRequest,
  type ReauthenticateRequest,
  type TokenRequest,
  type UpdateMyProfileRequest,
} from '@mfos/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  ApiError,
  apiGet,
  apiPath,
  apiPost,
  apiPostNoContent,
  apiPatch,
  apiRequest,
} from '../../lib/api-client';

export const authKeys = {
  me: ['auth', 'me'] as const,
  optionalMe: ['auth', 'me', 'optional'] as const,
  sessions: ['auth', 'sessions'] as const,
  mfa: ['auth', 'mfa'] as const,
};

const authPath = (...segments: string[]) => apiPath('auth', ...segments);

export function fetchCurrentUser(signal?: AbortSignal): Promise<CurrentUser> {
  return apiGet(authPath('me'), {
    schema: currentUserSchema,
    ...(signal !== undefined ? { signal } : {}),
  });
}

/** Session query for protected areas. A final 401 signs the user out (see SessionBridge). */
export function useCurrentUser() {
  return useQuery({
    queryKey: authKeys.me,
    queryFn: ({ signal }) => fetchCurrentUser(signal),
    staleTime: 60_000,
    retry: false,
  });
}

/** The signed-in user's IANA time zone for displaying instants. */
export function useUserTimeZone(): string | undefined {
  return useCurrentUser().data?.timezone;
}

/** Probes for an existing session on public pages without triggering a global sign-out. */
export function useOptionalCurrentUser() {
  return useQuery({
    queryKey: authKeys.optionalMe,
    queryFn: async ({ signal }): Promise<CurrentUser | null> => {
      try {
        return await apiRequest(authPath('me'), {
          schema: currentUserSchema,
          signal,
          notifySessionExpired: false,
        });
      } catch (error) {
        if (error instanceof ApiError && error.code === 'UNAUTHENTICATED') {
          return null;
        }
        throw error;
      }
    },
    retry: false,
  });
}

export const login = (body: LoginRequest): Promise<LoginResponse> =>
  apiPost(authPath('login'), body, loginResponseSchema);

export const loginWithMfa = (body: MfaLoginRequest): Promise<LoginResponse> =>
  apiPost(authPath('login', 'mfa'), body, loginResponseSchema);

export const requestMagicLink = (body: EmailRequest) =>
  apiPost(authPath('magic-link'), body, acceptedResponseSchema);

export const consumeMagicLink = (body: TokenRequest): Promise<LoginResponse> =>
  apiPost(authPath('magic-link', 'consume'), body, loginResponseSchema);

export const requestPasswordReset = (body: EmailRequest) =>
  apiPost(authPath('password', 'forgot'), body, acceptedResponseSchema);

export const resetPassword = (body: PasswordResetRequest) =>
  apiPost(authPath('password', 'reset'), body, acceptedResponseSchema);

export const reauthenticate = (body: ReauthenticateRequest): Promise<CurrentUser> =>
  apiPost(authPath('reauthenticate'), body, currentUserSchema);

/** Stores the signed-in user and drops any data cached for a previous session. */
export function useCompleteSignIn() {
  const queryClient = useQueryClient();
  return useCallback(
    (user: CurrentUser) => {
      queryClient.clear();
      queryClient.setQueryData(authKeys.me, user);
    },
    [queryClient],
  );
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPostNoContent(authPath('logout')),
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMyProfileRequest) => apiPatch(authPath('me'), body, currentUserSchema),
    onSuccess: (user) => {
      queryClient.setQueryData(authKeys.me, user);
    },
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PasswordChangeRequest) =>
      apiPost(authPath('password', 'change'), body, currentUserSchema),
    onSuccess: (user) => {
      queryClient.setQueryData(authKeys.me, user);
      void queryClient.invalidateQueries({ queryKey: authKeys.sessions });
    },
  });
}

export function useDeviceSessions() {
  return useQuery({
    queryKey: authKeys.sessions,
    queryFn: ({ signal }) =>
      apiGet(authPath('sessions'), { schema: deviceSessionListSchema, signal }),
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => apiPostNoContent(authPath('sessions', sessionId, 'revoke')),
    onSettled: () => queryClient.invalidateQueries({ queryKey: authKeys.sessions }),
  });
}

export function useRevokeOtherSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost(authPath('sessions', 'revoke-others'), undefined, revokedCountSchema),
    onSettled: () => queryClient.invalidateQueries({ queryKey: authKeys.sessions }),
  });
}

export function useMfaStatus() {
  return useQuery({
    queryKey: authKeys.mfa,
    queryFn: ({ signal }) => apiGet(authPath('mfa'), { schema: mfaStatusSchema, signal }),
  });
}

export function useStartTotpSetup() {
  return useMutation({
    mutationFn: () => apiPost(authPath('mfa', 'totp', 'setup'), undefined, totpSetupResponseSchema),
  });
}

function useMfaChanged() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: authKeys.mfa }),
      queryClient.invalidateQueries({ queryKey: authKeys.me }),
      queryClient.invalidateQueries({ queryKey: ['workspaces'] }),
    ]);
}

export function useConfirmTotp() {
  const onChanged = useMfaChanged();
  return useMutation({
    mutationFn: (body: MfaCodeRequest) =>
      apiPost(authPath('mfa', 'totp', 'confirm'), body, recoveryCodesResponseSchema),
    onSuccess: onChanged,
  });
}

export function useDisableTotp() {
  const onChanged = useMfaChanged();
  return useMutation({
    mutationFn: (body: MfaCodeRequest) =>
      apiPost(authPath('mfa', 'totp', 'disable'), body, mfaStatusSchema),
    onSuccess: onChanged,
  });
}

export function useRegenerateRecoveryCodes() {
  const onChanged = useMfaChanged();
  return useMutation({
    mutationFn: (body: MfaCodeRequest) =>
      apiPost(authPath('mfa', 'recovery-codes'), body, recoveryCodesResponseSchema),
    onSuccess: onChanged,
  });
}
